import { ClinicalEventEntry, type EntryDescriptor } from './ClinicalEventEntry';
import { activeBirthEvents } from './birthCalculations';
import { ballardComponentRanges, ballardScore, cchdScreen, type CchdAttempt } from './newbornScreening';
import { formatInstant } from '@/lib/utils/date';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { ClinicalEvent, ClinicalSubject } from './types';
import type { PostpartumDyadProps } from './PostpartumDyad';
const screenFields: EntryDescriptor[] = [
  {
    eventType: 'NB.SCREEN',
    schemaVersion: 2,
    label: 'CCHD screening',
    initialPayload: { type: 'CCHD' },
    fields: [
      { key: 'right_hand_pct', label: 'Right hand SpO₂ (%)', kind: 'number' },
      { key: 'foot_pct', label: 'Foot SpO₂ (%)', kind: 'number' },
      { key: 'room_air', label: 'Measured in room air', kind: 'boolean' },
      { key: 'early_discharge', label: 'Screen before 24 hours due to early discharge', kind: 'boolean' },
      { key: 'attempt_number', label: 'Attempt number', kind: 'number' },
      { key: 'result', label: 'Documented result / clinical review', kind: 'text' },
    ],
  },
];
const ballardFields: EntryDescriptor[] = [
  {
    eventType: 'NB.BALLARD',
    label: 'New Ballard numeric assessment',
    fields: Object.entries(ballardComponentRanges).map(([key, range]) => ({
      key,
      label: `${key.replaceAll('_', ' ')} score (${range[0]} to ${range[1]})`,
      kind: 'number',
    })),
  },
];
export function NewbornScreeningPanel({
  mode,
  subject,
  birthAt,
  events,
  episodeId,
  timeZone,
  configuration,
  disabled,
  onSave,
}: {
  mode: 'cchd' | 'ballard';
  subject: ClinicalSubject;
  birthAt?: string;
  events: ClinicalEvent[];
  episodeId: string;
  timeZone: string;
  configuration?: PostpartumDyadProps['configuration'];
  disabled?: boolean;
  onSave: PostpartumDyadProps['onSave'];
}) {
  const confirmed = activeBirthEvents(events, episodeId, new Date().toISOString()).filter(
    (e) => e.subject_id === subject.id,
  );
  const policy = configuration?.clinically_reviewed ? configuration.newborn_screening : undefined;
  const records = confirmed.filter((e) => e.event_type === 'NB.SCREEN' && e.payload.type === 'CCHD');
  const attempts: CchdAttempt[] = records
    .filter(
      (e) =>
        typeof e.payload.right_hand_pct === 'number' &&
        typeof e.payload.foot_pct === 'number' &&
        typeof e.payload.room_air === 'boolean' &&
        typeof e.payload.early_discharge === 'boolean',
    )
    .map((e) => ({
      id: e.id,
      subject_id: e.subject_id,
      at: e.occurred_at,
      right_hand_pct: e.payload.right_hand_pct as number,
      foot_pct: e.payload.foot_pct as number,
      room_air: e.payload.room_air as boolean,
      early_discharge: e.payload.early_discharge as boolean,
    }));
  const cchd =
    records.length !== attempts.length ? null : cchdScreen(attempts, subject.id, birthAt ?? '', policy?.cchd);
  const exam = confirmed.filter((e) => e.event_type === 'NB.BALLARD').at(-1);
  const ballard = exam ? ballardScore(exam.payload, exam.occurred_at, birthAt ?? '', policy?.ballard) : null;
  return (
    <div className="flex flex-col gap-4">
      {mode === 'cchd' ? (
        <>
          <p>
            Record each screening attempt separately. The reviewed algorithm uses both measurement sites, room-air
            status, age, and prior attempts.
          </p>
          {cchd?.value ? (
            <Alert>
              <AlertDescription>
                Calculated screening status: {cchd.value.status.replaceAll('_', ' ')}
                {cchd.value.repeat_at_ms
                  ? ` · Repeat due ${formatInstant(new Date(cchd.value.repeat_at_ms).toISOString(), 'MMM d, yyyy HH:mm:ss', timeZone)}`
                  : ''}
                . Review with the source observations. {cchd.warnings.join(' ')}
                Source: {policy?.cchd.citation}
              </AlertDescription>
            </Alert>
          ) : (
            <p>
              No calculated interpretation:{' '}
              {cchd?.warnings.join(' ') ??
                'Complete screening observations and a reviewed practice policy are required.'}
            </p>
          )}
        </>
      ) : (
        <>
          <p>
            Enter scores from the clinician’s completed New Ballard assessment.{' '}
            <a href="https://www.ballardscore.com/" target="_blank" rel="noreferrer">
              Assessment reference
            </a>
          </p>
          {ballard?.value ? (
            <p>
              Total {ballard.value.total}; assessment GA {ballard.value.ga_weeks} weeks. This does not change pregnancy
              dating. {ballard.warnings.join(' ')} Source: {policy?.ballard.citation}
            </p>
          ) : (
            <p>{ballard?.warnings.join(' ') ?? 'No complete reviewed assessment available.'}</p>
          )}
        </>
      )}
      <ClinicalEventEntry
        title={mode === 'cchd' ? 'CCHD screening' : 'New Ballard assessment'}
        descriptors={mode === 'cchd' ? screenFields : ballardFields}
        subjects={[subject]}
        timeZone={timeZone}
        disabled={disabled}
        onSave={onSave}
      />
    </div>
  );
}
